import { ChangeEvent, useEffect, useRef, useState } from 'react'

import Icon from '../Icons'
import FilterItem from './FilterItem'
import { FilterItemOption, FilterOptionBase, FilterProps } from './types'

function Filter<T extends FilterOptionBase = FilterOptionBase>({
  additionalClass,
  buildingImage,
  buttonClass = '',
  closeAfterSelect,
  config,
  disabled,
  handleSearch,
  label,
  name,
  onChange,
  options,
  placeholder,
  renderDropdownItem,
  renderListItem,
  searchValue,
  sortSelectedFirst = false,
  value = [],
  variant,
}: FilterProps<T>) {
  const { displayValuesList = false, hasSearch = false, multiple = false } = config ?? {}

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [opened, setOpened] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  // Selection snapshot taken on open, so rows don't jump while the user is (un)checking them
  const [selectedIdsAtOpen, setSelectedIdsAtOpen] = useState<Set<T['id']>>(new Set())

  const shouldDisplaySelectedAllPlaceholder =
    multiple && !searchValue && options.length > 0 && value.length === options.length

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpened(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (item: T) => {
    const isSelected = value.some((el) => el.id === item.id)
    const isSelectAll = !!item.selectAll

    if (multiple) {
      if (isSelectAll) {
        const onlyRealOptions = options.filter((el) => !el.selectAll)
        onChange({
          target: {
            name,
            value: value.length !== onlyRealOptions.length ? (onlyRealOptions as T[]) : ([] as T[]),
          },
        })
      } else {
        onChange({
          target: {
            name,
            value: (isSelected ? value.filter((el) => el.id !== item.id) : [...value, item]) as T[],
          },
        })
      }
    } else {
      onChange({ target: { name, value: item } })
      if (closeAfterSelect) setOpened(false)
    }
  }

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    handleSearch ? handleSearch(e.target.value) : setSearch(e.target.value)
  }

  const renderSearch = () => (
    <span>
      <input
        autoComplete="off"
        className="ctp-dropdown__content-search"
        name="search"
        placeholder="🔍 Search"
        type="text"
        value={handleSearch ? searchValue : search}
        onChange={onSearch}
      />
    </span>
  )

  const renderValuesList = () => {
    if (value.length === 0) return <p>No items selected.</p>

    const selectAllOption = options.find((el) => el.selectAll)
    if (selectAllOption && options.length - 1 === value.length) {
      return <FilterItem key={selectAllOption.id} item={selectAllOption} variant="list" />
    }

    return value.map((item, index) =>
      renderListItem ? (
        renderListItem({ index, item })
      ) : (
        <FilterItem key={item.id} item={item} variant="list" />
      ),
    )
  }

  const toggleOpened = () => {
    if (!opened && sortSelectedFirst) setSelectedIdsAtOpen(new Set(value.map((el) => el.id)))
    setOpened((prev) => !prev)
  }

  const searchedOptions = options.filter(
    (item) => !search || item.text.toLowerCase().includes(search.toLowerCase()),
  )

  // "Select all" stays on top, then the options selected at open time, then the rest
  const sortRank = (item: T) => (item.selectAll ? 0 : selectedIdsAtOpen.has(item.id) ? 1 : 2)
  const filteredOptions = sortSelectedFirst
    ? [...searchedOptions].sort((a, b) => sortRank(a) - sortRank(b))
    : searchedOptions

  return (
    <div
      ref={wrapperRef}
      className={`ctp-sidebar-section ${additionalClass || ''} ${disabled ? 'disabled' : ''} ${
        variant ? `ctp-dropdown--${variant}` : ''
      }`}
    >
      {label && (
        <label className="label" htmlFor={name}>
          {label}
        </label>
      )}

      <div className="ctp-dropdown">
        <button
          className={`ctp-dropdown__button ${buttonClass}`}
          disabled={disabled}
          type="button"
          onClick={toggleOpened}
        >
          {buildingImage && <img src={buildingImage} className="ctp-dropdown__image" alt="" />}
          <span
            className="ctp-dropdown__placeholder"
            style={{ maxWidth: multiple ? '90%' : '100%' }}
          >
            {shouldDisplaySelectedAllPlaceholder
              ? options.find((option) => option.selectAll)?.text || 'Selected all'
              : placeholder}
          </span>
          <Icon className="ctp-dropdown__button-control" icon="arrow" />
        </button>

        {opened && (
          <div className="ctp-dropdown__content ctp-dropdown__content--active">
            {hasSearch && renderSearch()}
            <div className="ctp-dropdown__options">
              {filteredOptions.map((item, index) =>
                renderDropdownItem ? (
                  renderDropdownItem({ index, item })
                ) : (
                  <FilterItem
                    key={item.id}
                    isSelected={value.some((el) => el.id === item.id)}
                    item={item}
                    variant="dropdown"
                    onSelect={(selected: FilterItemOption) => handleSelect(selected as T)}
                  />
                ),
              )}
            </div>
          </div>
        )}
      </div>

      {displayValuesList && <div className="ctp-sidebar-selection">{renderValuesList()}</div>}
    </div>
  )
}

export default Filter
